import React from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Smartphone, MessageSquare, Star } from 'lucide-react';
export const Badge = ({
  children,
  color = 'lime',
  className = ''
}: {
  children: React.ReactNode;
  color?: 'lime' | 'orange' | 'crimson' | 'gold' | 'slate';
  className?: string;
}) => {
  const colors = {
    lime: 'bg-lime text-charcoal',
    orange: 'bg-orange text-white',
    crimson: 'bg-crimson text-white',
    gold: 'bg-gold text-charcoal',
    slate: 'bg-slate text-charcoal'
  };
  return <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold font-mono uppercase tracking-wider ${colors[color]} ${className}`} data-id="element-426">
      {children}
    </span>;
};
export const SectionHeading = ({
  title,
  subtitle,
  className = ''
}: {
  title: string;
  subtitle?: string;
  className?: string;
}) => <div className={`mb-12 ${className}`} data-id="element-427">
    <motion.h2 initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="text-5xl md:text-7xl font-black font-heading tracking-tight mb-4 -skew-x-6" data-id="element-428">
      {title}
    </motion.h2>
    {subtitle && <motion.p initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.1
  }} className="text-xl md:text-2xl text-slate max-w-3xl" data-id="element-429">
        {subtitle}
      </motion.p>}
  </div>;
export const AudioPlayerPill = ({
  isPlaying,
  onClick,
  characterName
}: {
  isPlaying: boolean;
  onClick: () => void;
  characterName?: string;
}) => {
  return <button onClick={onClick} className="flex items-center gap-3 bg-dark border border-white/10 rounded-full px-4 py-2 hover:border-lime hover:shadow-[0_0_15px_rgba(200,255,0,0.2)] transition-all group" data-id="element-430">
      <div className="w-8 h-8 rounded-full bg-lime/20 flex items-center justify-center text-lime group-hover:bg-lime group-hover:text-charcoal transition-colors" data-id="element-431">
        {isPlaying ? <Pause size={16} className="fill-current" data-id="element-432" /> : <Play size={16} className="fill-current ml-1" data-id="element-433" />}
      </div>

      <div className="flex items-end gap-1 h-6" data-id="element-434">
        {[1, 2, 3, 4, 5].map(i => <motion.div key={i} animate={isPlaying ? {
        height: ['8px', '24px', '8px']
      } : {
        height: '8px'
      }} transition={{
        duration: 1,
        repeat: Infinity,
        delay: i * 0.1
      }} className="w-1.5 bg-lime rounded-full" style={{
        height: '8px'
      }} data-id="element-435" />)}
      </div>

      {characterName && <span className="text-sm font-bold ml-2 text-slate group-hover:text-white transition-colors" data-id="element-436">
          {characterName}
        </span>}
    </button>;
};
export const PhoneMockup = ({
  message,
  sender
}: {
  message: string;
  sender: string;
}) => {
  return <div className="relative mx-auto w-[300px] h-[600px] bg-charcoal border-[12px] border-dark rounded-[40px] shadow-2xl overflow-hidden flex flex-col" data-id="element-437">
      {/* Notch */}
      <div className="absolute top-0 inset-x-0 h-6 bg-dark rounded-b-3xl w-40 mx-auto z-20" data-id="element-438"></div>

      {/* Screen */}
      <div className="flex-1 bg-[#1A1A1A] p-4 pt-12 flex flex-col gap-4 relative z-10" data-id="element-439">
        <div className="flex items-center justify-between mb-4" data-id="element-440">
          <span className="text-xs font-mono text-slate" data-id="element-441">4:00 PM</span>
          <div className="flex gap-1" data-id="element-442">
            <div className="w-4 h-3 bg-white rounded-sm" data-id="element-443"></div>
            <div className="w-5 h-3 bg-white rounded-sm" data-id="element-444"></div>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        y: 0,
        scale: 1
      }} viewport={{
        once: true
      }} className="bg-dark border border-white/10 rounded-2xl p-4 shadow-lg" data-id="element-445">
          <div className="flex items-center gap-2 mb-2" data-id="element-446">
            <div className="w-8 h-8 rounded-full bg-lime flex items-center justify-center text-charcoal" data-id="element-447">
              <MessageSquare size={16} data-id="element-448" />
            </div>
            <span className="font-bold text-sm" data-id="element-449">{sender}</span>
          </div>
          <p className="text-sm text-slate leading-relaxed" data-id="element-450">{message}</p>
        </motion.div>
      </div>
    </div>;
};
export const TestimonialCard = ({
  quote,
  author,
  leagueType,
  rating = 5
}: {
  quote: string;
  author: string;
  leagueType: string;
  rating?: number;
}) => <motion.div whileHover={{
  y: -5
}} className="bg-dark border border-white/10 rounded-2xl p-6 md:p-8 flex flex-col gap-4" data-id="element-451">
    <div className="flex gap-1 text-gold" data-id="element-452">
      {[...Array(rating)].map((_, i) => <Star key={i} size={20} className="fill-current" data-id="element-453" />)}
    </div>
    <p className="text-lg md:text-xl font-medium italic text-white flex-1" data-id="element-454">
      "{quote}"
    </p>
    <div data-id="element-455">
      <p className="font-bold text-lime" data-id="element-456">{author}</p>
      <p className="text-sm text-slate font-mono" data-id="element-457">{leagueType}</p>
    </div>
  </motion.div>;